import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { getTranslation } from '../../translations/translations';
import './ForgotPassword.css';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  
  // Get language from localStorage
  const currentLanguage = localStorage.getItem('preferred-language') || 'en';
  const t = (key, fallback) => getTranslation(key, currentLanguage, fallback);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email.trim()) {
      setError(t('emailRequired', 'Please enter your email address'));
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError(t('invalidEmailFormat', 'Please enter a valid email address'));
      return;
    }

    setLoading(true);
    setError('');

    try {
      await axios.post('/api/auth/forgot-password', { email: email.trim() });
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.message || t('passwordResetError', 'An error occurred. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="forgot-password-container">
        <div className="forgot-password-wrapper">
          <div className="forgot-password-header">
            <div className="success-icon">✓</div>
            <h2 className="forgot-password-title">{t('checkYourEmail', 'Check Your Email')}</h2>
          </div>
          <div className="success-message">
            <p>{t('resetLinkSent', 'If an account with that email exists, you will receive a password reset link shortly. Please also check your spam folder.')}</p>
            <p className="note">{t('linkExpiresIn', 'The link will expire in 1 hour.')}</p>
          </div>
          <Link to="/" className="back-to-login-button">
            {t('backToLogin', 'Back to Login')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="forgot-password-container">
      <div className="forgot-password-wrapper">
        <div className="forgot-password-header">
          <h2 className="forgot-password-title">{t('forgotPasswordTitle', 'Forgot Password')}</h2>
          <p className="forgot-password-subtitle">
            {t('forgotPasswordSubtitle', 'Enter your email address and we will send you a link to reset your password.')}
          </p>
        </div>

        {error && (
          <div className="error-message">
            <span className="error-icon">⚠️</span>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="forgot-password-form">
          <div className="input-group">
            <label className="input-label">{t('email', 'Email Address')}</label>
            <input
              type="email"
              placeholder={t('enterEmail', 'Enter your email')}
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setError('');
              }}
              className="forgot-password-input"
              disabled={loading}
              autoComplete="email"
              autoFocus
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="submit-button"
          >
            {loading ? (
              <>
                <span className="loading-spinner"></span>
                {t('sending', 'Sending...')}
              </>
            ) : (
              t('sendResetLink', 'Send Reset Link')
            )}
          </button>

          <Link to="/" className="back-link">
            ← {t('backToLogin', 'Back to Login')}
          </Link>
        </form>
      </div>
    </div>
  );
};

export default ForgotPassword;
